import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Phone } from "lucide-react";
import { api, API } from "@/lib/api";
import { LOGO_URL } from "@/lib/assets";

const imgSrc = (u) => (u?.startsWith("/api") ? `${API.replace("/api", "")}${u}` : u);

const IMG_BASE = "https://primary.jwwb.nl/public/o/r/n/temp-bkcxxqajnapgmncxuwyd";

const OFFICERS = [
  {
    role: "President",
    name: "Chris Hay",
    phone: "0427 565 956",
    tel: "0427565956",
    img: `${IMG_BASE}/received_1799452327609729-high.jpg?enable-io=true&enable=upscale&crop=1%3A1&width=800`,
  },
  {
    role: "Vice President",
    name: "Christopher Ross",
    phone: "0434 643 605",
    tel: "0434643605",
    img: `${IMG_BASE}/17880676102271641153144740346356-high.jpg?enable-io=true&enable=upscale&crop=0.6667%3A1&width=800`,
  },
  {
    role: "Secretary",
    name: "Callum Tanner",
    phone: "0487 413 183",
    tel: "0487413183",
    img: `${IMG_BASE}/20260503_130255-high.jpg?enable-io=true&enable=upscale&crop=1%3A1&width=800`,
  },
  {
    role: "Treasurer",
    name: "Kylie Divall",
    phone: "0432 058 538",
    tel: "0429987341",
    img: `${IMG_BASE}/20260830_154326-high.jpg?enable-io=true&enable=upscale&crop=0.7194%3A1&width=800`,
  },
];

const MEMBERS = ["Ian Ross", "Norman Fisher", "Sue Aurisch", "Bill Aurisch", "Jack Jordan"];

export default function Committee() {
  const [officers, setOfficers] = useState(OFFICERS);
  const [members, setMembers] = useState(MEMBERS);

  useEffect(() => {
    api
      .get("/committee")
      .then(({ data }) => {
        if (data.officers?.length) setOfficers(data.officers);
        if (data.members?.length) setMembers(data.members.map((m) => (typeof m === "string" ? m : m.name)));
      })
      .catch(() => {});
  }, []);

  return (
    <main className="min-h-screen bg-clay-bg">
      <header className="border-b border-clay-border bg-clay-surface/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <a data-testid="committee-logo" href="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Mid Western Speedway logo" className="w-10 h-10 object-cover rounded-sm ring-1 ring-clay-border" />
            <span className="font-display font-bold uppercase text-lg tracking-wide">
              MWS <span className="text-clay-orange">Committee</span>
            </span>
          </a>
          <a
            data-testid="committee-back-link"
            href="/"
            className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-clay-muted hover:text-clay-gold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </a>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-14 lg:py-20">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-4"
        >
          The People Behind the Club
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-display font-black uppercase tracking-tight text-4xl sm:text-6xl leading-none mb-4"
        >
          The Committee
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-clay-muted max-w-2xl mb-14"
        >
          Mortlake Car Club Inc. is run entirely by volunteers. Questions about memberships, nominations, sponsorship
          or anything speedway — these are the people to call.
        </motion.p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {officers.map((o, i) => (
            <motion.div
              key={o.name}
              data-testid="committee-card"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group border border-clay-border bg-clay-card overflow-hidden hover:border-clay-orange/60 transition-colors duration-500"
            >
              <div className="overflow-hidden">
                <img
                  src={imgSrc(o.img)}
                  alt={o.name}
                  className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-clay-orange mb-1.5">{o.role}</p>
                <h2 className="font-display font-bold uppercase text-2xl tracking-tight mb-3">{o.name}</h2>
                {o.phone && (
                  <a
                    data-testid={`committee-phone-${o.name.split(" ")[0].toLowerCase()}`}
                    href={`tel:${o.tel || o.phone.replace(/\s/g, "")}`}
                    className="flex items-center gap-2 text-clay-muted hover:text-clay-gold transition-colors font-mono text-sm"
                  >
                    <Phone className="w-4 h-4 text-clay-gold" /> {o.phone}
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="border border-clay-border bg-clay-surface/50 p-8 sm:p-12 text-center"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-6">
            Our other dedicated committee members
          </p>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3 mb-8">
            {members.map((m) => (
              <span key={m} data-testid="committee-member" className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight text-clay-text/90">
                {m}
              </span>
            ))}
          </div>
          <p className="text-clay-muted max-w-xl mx-auto">
            Without these guys' hard work and dedication, it would be nearly impossible to stay afloat.
            <span className="block text-clay-gold mt-2 font-display uppercase tracking-wide">Thank you, guys.</span>
          </p>
        </motion.div>
      </div>
    </main>
  );
}
